/**
 * SyntheticControlStudy — New 2-panel layout for study creation.
 *
 * Replaces the old multi-step form wizard with a conversational interface.
 *
 * Layout:
 *   ┌─ Chat (left ~40%) ─────┬─ Artifacts (right ~60%) ──────────────────┐
 *   │  AI guided setup       │  Stat tiles + Lift chart + Power curve    │
 *   │  CSV file upload       │  Market table + AI Insights               │
 *   └────────────────────────┴───────────────────────────────────────────┘
 *
 * Socket.IO: listens for study_job_complete → renders artifacts panel.
 * The existing view-mode page (GeoLiftStudy) is unaffected.
 */

import React, { useCallback, useState } from "react";
import { notification } from "antd";
import StudyChat from "./StudyChat";
import StudyArtifacts from "./StudyArtifacts";

interface SyntheticControlStudyProps {
  /** Pre-existing sessionId (from URL or redux) — null for new study */
  initialSessionId?: string | null;
  /** Called when the job is created so the parent can navigate to view mode */
  onJobConfigCreated?: (jobConfigId: string) => void;
}

const SyntheticControlStudy: React.FC<SyntheticControlStudyProps> = ({
  initialSessionId = null,
  onJobConfigCreated,
}) => {
  const [sessionId, setSessionId] = useState<string | null>(initialSessionId);
  const [jobConfigId, setJobConfigId] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [studyName, setStudyName] = useState<string | undefined>(undefined);
  const [notificationApi, notificationContext] = notification.useNotification();

  const handleSessionCreated = useCallback((sid: string) => {
    setSessionId(sid);
  }, []);

  const handleJobComplete = useCallback(
    (jcId: string) => {
      setJobConfigId(jcId);
      setIsComplete(true);

      // In-app notification
      notificationApi.success({
        message: "Study Complete! 🎉",
        description:
          "Your synthetic control analysis has finished. Results are now visible on the right panel.",
        duration: 8,
        placement: "topRight",
        style: {
          borderLeft: "4px solid var(--violet)",
          borderRadius: "12px",
          background: "var(--bg-elevated)",
          color: "var(--text-primary)",
        },
      });

      onJobConfigCreated?.(jcId);
    },
    [notificationApi, onJobConfigCreated]
  );

  return (
    <>
      {notificationContext}

      {/* ── 2-Panel split layout ──────────────────────────────────────── */}
      <div
        className="flex h-full overflow-hidden rounded-xl border shadow-sm"
        style={{ borderColor: "var(--border-default)", background: "var(--bg-surface)" }}
      >

        {/* ── Left: Chat panel ─────────────────────────────────────────── */}
        <div
          className="shrink-0 flex flex-col"
          style={{ width: isComplete ? "35%" : "42%", minWidth: 300, transition: "width 0.4s ease" }}
        >
          <StudyChat
            sessionId={sessionId}
            onSessionCreated={handleSessionCreated}
            onJobComplete={handleJobComplete}
          />
        </div>

        {/* ── Divider ───────────────────────────────────────────────────── */}
        <div className="w-px shrink-0" style={{ background: "var(--border-default)" }} />

        {/* ── Right: Artifacts panel ────────────────────────────────────── */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <StudyArtifacts
            jobConfigId={jobConfigId}
            isComplete={isComplete}
            studyName={studyName}
          />
        </div>
      </div>
    </>
  );
};

export default SyntheticControlStudy;
